import type { WorkspaceApp } from "@bafe/types";

import { stripAppBasename } from "./auth-flow.js";
import { parseWorkspaceLocation, workspaceRestPath } from "./workspace-url.js";

export function isManageWorkspacePath(pathname: string): boolean {
	const rest = workspaceRestPath(appRoutePath(pathname));
	return rest === "/manage" || rest.startsWith("/manage/");
}

/** Path inside the SPA (strips the Vite `/platform` static prefix when present). */
export function appRoutePath(pathname: string): string {
	return stripAppBasename(pathname.split("?")[0] ?? pathname);
}

/** Top-level product apps inside Key Workspace (Manage is always available). */
export const WORKSPACE_PRODUCT_APPS = [
	"manage",
	"hub",
	"client",
	"mail",
	"team",
	"phone",
	"engage",
	"social",
	"site",
] as const;

export type WorkspaceProductApp = (typeof WORKSPACE_PRODUCT_APPS)[number];

/** Navigation context: platform administration vs workspace applications. */
export type PlatformRealm = "platform" | "workspace";

const WORKSPACE_APP_IDS = new Set<WorkspaceApp>(["hub", "mail", "site", "team", "social", "engage"]);

export function isWorkspaceProductApp(value: string): value is WorkspaceProductApp {
	return (WORKSPACE_PRODUCT_APPS as readonly string[]).includes(value);
}

export function workspaceProductToWorkspaceApp(app: WorkspaceProductApp): WorkspaceApp | null {
	if (app === "manage" || app === "client" || app === "phone") return null;
	return WORKSPACE_APP_IDS.has(app as WorkspaceApp) ? (app as WorkspaceApp) : null;
}

export function isPlatformRestPath(restPath: string): boolean {
	return restPath === "/platform" || restPath.startsWith("/platform/");
}

export function realmOf(pathname: string): PlatformRealm {
	const path = pathname.split("?")[0] ?? pathname;
	if (path === "/platform" || path.startsWith("/platform/")) return "platform";
	const rest = workspaceRestPath(path);
	if (isPlatformRestPath(rest)) return "platform";
	return "workspace";
}

export function workspaceSectionActive(pathname: string, section: string): boolean {
	const rest = workspaceRestPath(appRoutePath(pathname));
	const base = `/${section}`;
	return rest === base || rest.startsWith(`${base}/`);
}

export function workspaceAppOf(pathname: string): WorkspaceProductApp {
	const path = workspaceRestPath(appRoutePath(pathname));
	if (isPlatformRestPath(path)) return "manage";
	if (path === "/" || path === "") return "hub";
	if (path === "/hub" || path.startsWith("/hub/")) return "hub";
	if (path === "/client" || path.startsWith("/client/")) return "client";
	if (path === "/mail" || path.startsWith("/mail/")) return "mail";
	if (path === "/team" || path.startsWith("/team/")) return "team";
	if (path === "/phone" || path.startsWith("/phone/")) return "phone";
	if (path === "/engage" || path.startsWith("/engage/")) return "engage";
	if (path === "/social" || path.startsWith("/social/")) return "social";
	if (path === "/site" || path.startsWith("/site/")) return "site";
	return "manage";
}

/** Global Platform Workspace — platform operators only, not organisation owners. */
export function canOpenPlatformWorkspace(input: { platformOwner: boolean }): boolean {
	return input.platformOwner;
}

export const USER_SETTINGS_SECTIONS = ["profile", "security", "notifications", "sessions", "data"] as const;

export function isUserSettingsPath(pathname: string): boolean {
	const path = appRoutePath(pathname);
	if (path === "/user/settings") return true;
	if (!path.startsWith("/user/settings/")) return false;
	const section = path.slice("/user/settings/".length).split("/")[0] ?? "";
	return (USER_SETTINGS_SECTIONS as readonly string[]).includes(section);
}

/** Profile, billing, users, and workspace picker — user dashboard (not Platform Workspace). */
export function isPersonalAccountPath(pathname: string): boolean {
	const path = appRoutePath(pathname);
	if (isUserSettingsPath(path)) return true;
	return path === "/user/workspaces" || path === "/user/billing" || path === "/user/users";
}

function isAiPath(pathname: string): boolean {
	const path = appRoutePath(pathname);
	return path === "/ai" || path.startsWith("/ai/");
}

/** Which workspace product app the sidebar should reflect (preserves context on user-level pages). */
export function sidebarWorkspaceApp(
	pathname: string,
	pathArea: WorkspaceProductApp,
	lastArea: WorkspaceProductApp,
): WorkspaceProductApp {
	if (isAiPath(pathname) || isPersonalAccountPath(pathname)) return lastArea;
	if (isManageWorkspacePath(pathname)) return lastArea;
	return pathArea;
}

/** Organisation id highlighted in the switcher — only when the URL is under `/workspace/:slug`. */
export function workspaceSwitcherActiveOrganisationId(
	pathname: string,
	memberships: ReadonlyArray<{ organisationId: string; organisationSlug: string }>,
): string | null {
	const parsed = parseWorkspaceLocation(appRoutePath(pathname));
	if (!parsed) return null;
	return memberships.find((membership) => membership.organisationSlug === parsed.slug)?.organisationId ?? null;
}

export function shouldPersistWorkspaceApp(pathname: string): boolean {
	if (isAiPath(pathname) || isPersonalAccountPath(pathname)) return false;
	if (isManageWorkspacePath(pathname)) return false;
	return realmOf(pathname) === "workspace";
}

export const LAST_WORKSPACE_APP_STORAGE_KEY = "bafe:lastWorkspaceApp";

export function readPersistedWorkspaceApp(): WorkspaceProductApp {
	if (typeof sessionStorage === "undefined") return "hub";
	try {
		const stored = sessionStorage.getItem(LAST_WORKSPACE_APP_STORAGE_KEY);
		if (stored && isWorkspaceProductApp(stored)) return stored;
	} catch {
		// ignore
	}
	return "hub";
}

export function persistWorkspaceApp(app: WorkspaceProductApp): void {
	if (typeof sessionStorage === "undefined") return;
	try {
		sessionStorage.setItem(LAST_WORKSPACE_APP_STORAGE_KEY, app);
	} catch {
		// ignore
	}
}

/** Legacy `/manage/account` paths kept for redirects and mail-only allowances. */
export function isLegacyUserAccountPath(pathname: string): boolean {
	const path = pathname.split("?")[0] ?? pathname;
	if (path === "/manage/account") return true;
	return path.startsWith("/manage/account/");
}

export function legacyUserAccountRedirect(pathname: string): string | null {
	const path = pathname.split("?")[0] ?? pathname;
	if (path === "/manage/account") return "/user/settings/profile";
	if (!path.startsWith("/manage/account/")) return null;
	const rest = path.slice("/manage/account/".length);
	return `/user/settings/${rest}`;
}
